import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Link,
  IconButton,
  Breadcrumbs,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { OpenInNew, Visibility } from '@mui/icons-material';
import { useRecentDeployments } from '../../contexts/DeploymentStatusContext';
import {
  formatDate,
  getDuration,
  getCloudBuildUrl,
} from '../../utils/deploymentUtils';

type FilterType = 'all' | 'in_progress' | 'success' | 'failed';

function Deployments() {
  const navigate = useNavigate();
  const { deployments, loading, error } = useRecentDeployments();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const handleDeploymentClick = (deploymentId: string) => {
    navigate(`/deployment/${deploymentId}`);
  };

  const filteredDeployments = useMemo(() => {
    if (activeFilter === 'all') {
      return deployments;
    }
    return deployments.filter(deployment => deployment.status === activeFilter);
  }, [deployments, activeFilter]);

  const getStatusCount = (status: string) => {
    return deployments.filter(deployment => deployment.status === status).length;
  };

  // DataGrid columns configuration
  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const getStatusColor = (status: string) => {
          switch (status) {
            case 'success':
              return 'success';
            case 'failed':
              return 'error';
            case 'in_progress':
            case 'pending':
              return 'warning';
            default:
              return 'default';
          }
        };

        return (
            <Chip
              label={params.value.replace('_', ' ')}
              color={getStatusColor(params.value)}
              size="small"
              variant="outlined"
            />
        );
      },
    },
    {
      field: 'module',
      headerName: 'Module',
      width: 120,
    },
    {
      field: 'environment',
      headerName: 'Environment',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: 'branch',
      headerName: 'Branch',
      width: 150,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'pullRequest',
      headerName: 'Pull Request',
      width: 200,
      display: 'flex',
      renderCell: (params) => {
        const { prNumber, prTitle } = params.row;
        if (prNumber) {
          return (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                #{prNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {prTitle}
              </Typography>
            </Box>
          );
        }
        return <Typography variant="body2" color="text.secondary">-</Typography>;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 120,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {getDuration(params.row.createdAt, params.row.finishedAt)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={(e) => {
              e.stopPropagation();
              handleDeploymentClick(params.row.deploymentId);
            }}
          >
            Details
          </Button>
          {params.row.buildId && (
            <IconButton
              size="small"
              component={Link}
              href={getCloudBuildUrl(params.row.buildId, params.row.environment)}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  // Transform data for DataGrid
  const rows = filteredDeployments.map((deployment) => ({
    id: deployment.deploymentId,
    ...deployment,
    pullRequest: deployment.prNumber ? `#${deployment.prNumber}` : '-',
  }));

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading deployments...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/pull-requests')}
            sx={{ textDecoration: 'none' }}
          >
            Pull Requests
          </Link>
          <Typography variant="body2" color="text.primary">
            Deployments
          </Typography>
        </Breadcrumbs>

        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Deployments
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Real-time deployment status and history
          </Typography>
        </Box>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filter Tabs */}
      {deployments.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={activeFilter}
            onChange={(_, value) => setActiveFilter(value)}
            aria-label="deployment filters"
          >
            <Tab
              label={`All Deployments (${deployments.length})`}
              value="all"
            />
            <Tab
              label={`In Progress (${getStatusCount('in_progress')})`}
              value="in_progress"
            />
            <Tab
              label={`Successful (${getStatusCount('success')})`}
              value="success"
            />
            <Tab
              label={`Failed (${getStatusCount('failed')})`}
              value="failed"
            />
          </Tabs>
        </Box>
      )}

      {/* DataGrid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          onRowClick={(params: GridRowParams) => {
            handleDeploymentClick(params.row.deploymentId);
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Empty State */}
      {filteredDeployments.length === 0 && !loading && !error && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {deployments.length === 0 
              ? 'No deployments found' 
              : `No deployments match the "${activeFilter}" filter`
            }
          </Typography>
          {deployments.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Deployments will appear here once you start deploying pull requests.
            </Typography>
          )}
        </Box>
      )}
    </Container>
  );
};

export default Deployments;